import { v4 } from "uuid";

import { OnsiteEditDto } from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";

import { IOnsiteEditsState } from "../types/onsiteEdits";

const sliceInitialOnsiteEditsState: IOnsiteEditsState = {
  isSuccess: false,
  allEdits: [],
  storedEdits: [],
};

const onsiteEdits = rtk.createSlice({
  name: "onsiteEdits",
  initialState: sliceInitialOnsiteEditsState,
  reducers: {
    resetOnsiteEdits: (draft) => {
      draft = sliceInitialOnsiteEditsState;
      return draft;
    },
    modClearOnsiteEdits: (draft) => {
      draft = { ...draft, allEdits: [] };
      return draft;
    },
    setOfferTestOnsiteEdits: (
      draft,
      action: PayloadAction<{
        onsiteEdits: OnsiteEditDto[];
        variationId: string;
      }>
    ) => {
      const updatedDraft = draft.allEdits.filter(
        (edit) => edit.variationId !== action.payload.variationId
      );

      const dupEdits: OnsiteEditDto[] = action.payload.onsiteEdits.map(
        (edit) => {
          return {
            ...edit,
            variationId: action.payload.variationId,
            id: v4(),
          };
        }
      );
      const onsiteEditGroups: Map<string, string> = new Map();
      const formatted = [...updatedDraft, ...dupEdits].map((onsiteEdit) => {
        const clone = { ...onsiteEdit };
        const groupKey = `${String(clone.find || "")}|${
          clone.isRegex
        }|${clone.querySelectors.join("|")}`;

        if (clone.groupId) {
          onsiteEditGroups.set(groupKey, clone.groupId);
        }

        if (onsiteEditGroups.has(groupKey)) {
          clone.groupId = onsiteEditGroups.get(groupKey)!;
        } else {
          const groupId = v4();
          clone.groupId = groupId;
          onsiteEditGroups.set(groupKey, groupId);
        }

        return clone;
      });

      draft.allEdits = formatted;
      return draft;
    },
    resetOfferTestOnsiteEdits: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.allEdits.filter(
        (edit) => edit.variationId !== action.payload.variationId
      );
      return draft;
    },
    initOnsiteEdits: (draft, action: PayloadAction<OnsiteEditDto[]>) => {
      draft.isSuccess = true;
      const onsiteEditGroups: Map<string, string> = new Map();
      const formatted = action.payload.map((onsiteEdit) => {
        const clone = { ...onsiteEdit };
        const groupKey = `${String(clone.find || "")}|${
          clone.isRegex
        }|${clone.querySelectors.join("|")}`;

        if (clone.groupId) {
          onsiteEditGroups.set(groupKey, clone.groupId);
        }

        if (onsiteEditGroups.has(groupKey)) {
          clone.groupId = onsiteEditGroups.get(groupKey)!;
        } else {
          const groupId = v4();
          clone.groupId = groupId;
          onsiteEditGroups.set(groupKey, groupId);
        }

        return clone;
      });
      draft.allEdits = formatted;
      draft.storedEdits = formatted;
      return draft;
    },
    deleteOnsiteEditGroup: (draft, action: PayloadAction<string>) => {
      const copyDraft = draft.allEdits.filter(
        (edit) => edit.groupId !== action.payload
      );
      draft.allEdits = copyDraft;
      return draft;
    },
    updateOnsiteEditGroup: (
      draft,
      action: PayloadAction<{
        groupId: string;
        value: any;
        key: keyof OnsiteEditDto;
      }>
    ) => {
      draft.allEdits.forEach((edit: OnsiteEditDto) => {
        if (edit.groupId === action.payload.groupId) {
          // @ts-ignore
          edit[action.payload.key] = action.payload.value;
        }
      });
      return draft;
    },
    updateOnsiteEdit: (
      draft,
      action: PayloadAction<{
        id: string;
        value: any;
        key: keyof OnsiteEditDto;
      }>
    ) => {
      draft.allEdits.forEach((edit) => {
        if (edit.id === action.payload.id) {
          // @ts-ignore
          edit[action.payload.key] = action.payload.value;
        }
      });
      return draft;
    },
  },
  selectors: {
    selectedOnsiteEdits: (state) => state.allEdits,
    selectedOnsiteEditsState: (state) => state,
  },
});

export const {
  initOnsiteEdits,
  resetOnsiteEdits,
  modClearOnsiteEdits,
  deleteOnsiteEditGroup,
  updateOnsiteEditGroup,
  updateOnsiteEdit,
  setOfferTestOnsiteEdits,
  resetOfferTestOnsiteEdits,
} = onsiteEdits.actions;

export const onsiteEditsReducer = onsiteEdits.reducer;
export const { selectedOnsiteEdits, selectedOnsiteEditsState } =
  onsiteEdits.selectors;

export const getVariationSpecificOnsiteEdits = (variationId: string) =>
  createSelector(selectedOnsiteEdits, (allEdits) => {
    // Filter the onsite edits based on the provided variationId
    return allEdits.filter((edit) => edit.variationId === variationId);
  });
